import React from 'react';

/**
 * Router × router table shared by the IS-IS cost and latency matrices:
 * rotated column headers, a sticky row-header column and a shaded
 * diagonal. Each matrix only decides what goes in an off-diagonal cell.
 */
interface Props {
    /** Router ids, in display order. */
    ids: string[];
    renderCell: (row: string, col: string) => React.ReactNode;
    /** Minimum cell width; latency values are wider than IGP costs. */
    minCellWidth?: number;
}

/** "KE.MXQRO01.RT01" → "MXQRO01.RT01". */
export function matrixRouterLabel(hostname: string): string {
    const parts = hostname.split('.');
    return parts.length >= 3 ? `${parts[1]}.${parts[2]}` : hostname;
}

const RouterMatrixGrid: React.FC<Props> = ({
    ids,
    renderCell,
    minCellWidth = 28,
}) => (
    <div className="overflow-auto">
        <table className="border-collapse text-xs">
            <thead>
                <tr>
                    <th
                        className="sticky left-0 z-10 bg-semantic-bg-primary
                            p-1 text-semantic-text-secondary font-medium
                            border border-semantic-border-secondary"
                        style={{ minWidth: 80 }}
                    />
                    {ids.map((col) => (
                        <th
                            key={col}
                            className="p-1 text-semantic-text-secondary
                                font-medium border
                                border-semantic-border-secondary
                                whitespace-nowrap"
                            style={{
                                writingMode: 'vertical-lr',
                                transform: 'rotate(180deg)',
                                minWidth: minCellWidth,
                                maxHeight: 100,
                            }}
                            title={col}
                        >
                            {matrixRouterLabel(col)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {ids.map((row) => (
                    <tr key={row}>
                        <td
                            className="sticky left-0 z-10
                                bg-semantic-bg-primary p-1
                                text-semantic-text-secondary
                                font-medium border
                                border-semantic-border-secondary
                                whitespace-nowrap"
                            title={row}
                        >
                            {matrixRouterLabel(row)}
                        </td>
                        {ids.map((col) =>
                            row === col ? (
                                <td
                                    key={col}
                                    className="p-1 border
                                        border-semantic-border-secondary
                                        text-center"
                                    style={{
                                        background:
                                            'var(--color-semantic-bg-secondary)',
                                    }}
                                >
                                    -
                                </td>
                            ) : (
                                <React.Fragment key={col}>
                                    {renderCell(row, col)}
                                </React.Fragment>
                            )
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default RouterMatrixGrid;
